import { IntentRoute } from '@/app/intents/IntentRoute'
import { Component, lazy, Suspense, useCallback, useEffect, useState, type ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { clearPendingIntent, postLoginPath, savePendingIntent } from '@/shared/lib/pendingIntentStorage'
import { LegacyDocumentSignRedirect, LEGACY_DOCUMENT_SIGN_PATH } from '@/app/LegacyDocumentSignRedirect'
import { AppError } from '@/shared/lib/appError'
import { Sentry } from '@/shared/lib/sentry'
import { logClientError } from '@/shared/lib/safeClientLog'
import { canUseAiChat } from '@/shared/lib/roleBasedUtils'
import { PageTransition } from '@/shared/ui/PageTransition'
import { useSessionStore } from '@/features/session/model/sessionStore'
import { OnboardingWelcome } from '@/shared/ui/OnboardingWelcome'
import { profilesRepository } from '@/shared/repositories/profiles/profilesRepository'
import { ErrorMessageWithCopy } from '@/shared/ui/ErrorMessageWithCopy'
import { HeartbeatLoader } from '@/shared/ui/HeartbeatLoader'
import { isChunkLoadError, recoverFromStaleDeployment } from '@/shared/pwa/staleDeploymentRecovery'
import { LoginPage } from '@/pages/LoginPage'
import { AppShell } from '@/widgets/navigation/AppShell'
import { NativeRouteGuard } from '@/app/NativeRouteGuard'
import { nativeFeatureFlags } from '@/shared/config/nativeFeatureFlags'
import { UnavailableInNativeApp } from '@/shared/ui/UnavailableInNativeApp'

const RegisterPage = lazy(() => import('@/pages/RegisterPage').then((m) => ({ default: m.RegisterPage })))
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })))
const ConfirmEmailPage = lazy(() => import('@/pages/ConfirmEmailPage').then((m) => ({ default: m.ConfirmEmailPage })))
const SelectProfilePage = lazy(() => import('@/pages/SelectProfilePage').then((m) => ({ default: m.SelectProfilePage })))
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })))
const PatientsPage = lazy(() => import('@/pages/PatientsPage').then((m) => ({ default: m.PatientsPage })))
const PatientDetailPage = lazy(() => import('@/pages/PatientDetailPage').then((m) => ({ default: m.PatientDetailPage })))
const PrescriptionsPage = lazy(() => import('@/pages/PrescriptionsPage').then((m) => ({ default: m.PrescriptionsPage })))
const InventoryPage = lazy(() => import('@/pages/InventoryPage').then((m) => ({ default: m.InventoryPage })))
const InventoryAddPage = lazy(() =>
  import('@/pages/inventory/InventoryAddPage').then((m) => ({ default: m.InventoryAddPage })),
)
const InventoryStockEditPage = lazy(() =>
  import('@/pages/inventory/InventoryStockEditPage').then((m) => ({ default: m.InventoryStockEditPage })),
)
const AddExamPage = lazy(() => import('@/pages/AddExamPage').then((m) => ({ default: m.AddExamPage })))
const AddEncounterPage = lazy(() => import('@/pages/AddEncounterPage').then((m) => ({ default: m.AddEncounterPage })))
const CalendarPage = lazy(() => import('@/pages/CalendarPage').then((m) => ({ default: m.CalendarPage })))
const NewsPage = lazy(() => import('@/pages/NewsPage').then((m) => ({ default: m.NewsPage })))
const PlantoesPage = lazy(() => import('@/pages/PlantoesPage').then((m) => ({ default: m.PlantoesPage })))
const PlantoesRegistrosPage = lazy(() =>
  import('@/pages/PlantoesRegistrosPage').then((m) => ({ default: m.PlantoesRegistrosPage })),
)
const LastResultsPage = lazy(() => import('@/pages/LastResultsPage').then((m) => ({ default: m.LastResultsPage })))
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })))
const MyProfilePage = lazy(() => import('@/pages/MyProfilePage').then((m) => ({ default: m.MyProfilePage })))
const CertificateSettings = lazy(() =>
  import('@/pages/doctor/CertificateSettings').then((m) => ({ default: m.CertificateSettings })),
)
const LocalAISettingsPage = lazy(() =>
  import('@/pages/doctor/LocalAISettingsPage').then((m) => ({ default: m.LocalAISettingsPage })),
)
const AdminUsersLicensesPage = lazy(() => import('@/pages/AdminUsersLicensesPage').then((m) => ({ default: m.AdminUsersLicensesPage })))
const AiProvidersAdminPage = lazy(() =>
  import('@/pages/settings/AiProvidersAdminPage').then((m) => ({ default: m.AiProvidersAdminPage })),
)
const PlatformPromptsAdminPage = lazy(() =>
  import('@/pages/admin/PlatformPromptsAdminPage').then((m) => ({ default: m.PlatformPromptsAdminPage })),
)
const ImportCenterPage = lazy(() => import('@/pages/ImportCenterPage').then((m) => ({ default: m.ImportCenterPage })))
const ImportPatientsPage = lazy(() => import('@/pages/ImportPatientsPage').then((m) => ({ default: m.ImportPatientsPage })))
const ImportExamsPage = lazy(() => import('@/pages/ImportExamsPage').then((m) => ({ default: m.ImportExamsPage })))
const ImportDocumentModelsPage = lazy(() =>
  import('@/pages/ImportDocumentModelsPage').then((m) => ({ default: m.ImportDocumentModelsPage })),
)
const AiPromptsConfigPage = lazy(() => import('@/pages/AiPromptsConfigPage').then((m) => ({ default: m.AiPromptsConfigPage })))
const ExamSharePage = lazy(() => import('@/pages/ExamSharePage').then((m) => ({ default: m.ExamSharePage })))
const PublicDocumentSharePage = lazy(() =>
  import('@/pages/PublicDocumentSharePage').then((m) => ({ default: m.PublicDocumentSharePage })),
)
const DispensationCounterPage = lazy(() =>
  import('@/pages/DispensationCounterPage').then((m) => ({ default: m.DispensationCounterPage })),
)
const DocumentationPage = lazy(() => import('@/pages/DocumentationPage').then((m) => ({ default: m.DocumentationPage })))
const ChangelogPage = lazy(() => import('@/pages/documentation/ui/ChangelogPage').then((m) => ({ default: m.ChangelogPage })))
const ChatIaPage = lazy(() => import('@/pages/ChatIaPage').then((m) => ({ default: m.ChatIaPage })))
const CadastrosPage = lazy(() => import('@/pages/CadastrosPage').then((m) => ({ default: m.CadastrosPage })))
const ExamTypesPage = lazy(() => import('@/pages/ExamTypesPage').then((m) => ({ default: m.ExamTypesPage })))
const ExamTypeEditPage = lazy(() => import('@/pages/ExamTypeEditPage').then((m) => ({ default: m.ExamTypeEditPage })))
const LocationsPage = lazy(() => import('@/pages/LocationsPage').then((m) => ({ default: m.LocationsPage })))
const LocationEditPage = lazy(() => import('@/pages/LocationEditPage').then((m) => ({ default: m.LocationEditPage })))
const AttendanceTypesPage = lazy(() =>
  import('@/pages/AttendanceTypesPage').then((m) => ({ default: m.AttendanceTypesPage })),
)
const AttendanceTypeEditPage = lazy(() =>
  import('@/pages/AttendanceTypeEditPage').then((m) => ({ default: m.AttendanceTypeEditPage })),
)
const ClinicalConductsPage = lazy(() =>
  import('@/pages/ClinicalConductsPage').then((m) => ({ default: m.ClinicalConductsPage })),
)
const ActiveIngredientsPage = lazy(() =>
  import('@/pages/ActiveIngredientsPage').then((m) => ({ default: m.ActiveIngredientsPage })),
)
const ActiveIngredientEditPage = lazy(() =>
  import('@/pages/ActiveIngredientEditPage').then((m) => ({ default: m.ActiveIngredientEditPage })),
)
const ActiveIngredientViewPage = lazy(() =>
  import('@/pages/ActiveIngredientViewPage').then((m) => ({ default: m.ActiveIngredientViewPage })),
)
const CIDCodesPage = lazy(() => import('@/pages/CIDCodesPage').then((m) => ({ default: m.CIDCodesPage })))
const CIDCodeEditPage = lazy(() =>
  import('@/pages/CIDCodeEditPage').then((m) => ({ default: m.CIDCodeEditPage })),
)
const ProcedureCodesPage = lazy(() =>
  import('@/pages/ProcedureCodesPage').then((m) => ({ default: m.ProcedureCodesPage })),
)
const ProcedureCodeEditPage = lazy(() =>
  import('@/pages/ProcedureCodeEditPage').then((m) => ({ default: m.ProcedureCodeEditPage })),
)
const ImportCIDPage = lazy(() => import('@/pages/ImportCIDPage').then((m) => ({ default: m.ImportCIDPage })))
const ImportProcedureCodesPage = lazy(() =>
  import('@/pages/ImportProcedureCodesPage').then((m) => ({ default: m.ImportProcedureCodesPage })),
)
const GoogleCalendarPage = lazy(() => import('@/pages/GoogleCalendarPage').then((m) => ({ default: m.GoogleCalendarPage })))
const GoogleCalendarCallbackPage = lazy(() => import('@/pages/GoogleCalendarCallbackPage').then((m) => ({ default: m.GoogleCalendarCallbackPage })))
const VidaasCallbackPage = lazy(() =>
  import('@/pages/auth/VidaasCallbackPage').then((m) => ({ default: m.VidaasCallbackPage })),
)
const ItiCallbackPage = lazy(() =>
  import('@/pages/auth/ItiCallbackPage').then((m) => ({ default: m.ItiCallbackPage })),
)
const AuthOAuthCallbackPage = lazy(() =>
  import('@/pages/auth/AuthOAuthCallbackPage').then((m) => ({ default: m.AuthOAuthCallbackPage })),
)
const PolicyPage = lazy(() => import('@/pages/PolicyPage').then((m) => ({ default: m.PolicyPage })))
const MedicationCalculatorPage = lazy(() =>
  import('@/pages/MedicationCalculatorPage').then((m) => ({ default: m.MedicationCalculatorPage })),
)
const MedicationDetailPage = lazy(() =>
  import('@/pages/MedicationDetailPage').then((m) => ({ default: m.MedicationDetailPage })),
)
const FeedbackPage = lazy(() => import('@/pages/FeedbackPage').then((m) => ({ default: m.FeedbackPage })))
const FeedPreferencesPage = lazy(() =>
  import('@/pages/FeedPreferencesPage').then((m) => ({ default: m.FeedPreferencesPage })),
)
const NotificationsPage = lazy(() =>
  import('@/pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
)
const AnatomyPage = lazy(() => import('@/pages/AnatomyPage').then((m) => ({ default: m.AnatomyPage })))
const OpenSourcePage = lazy(() =>
  import('@/pages/settings/OpenSourcePage').then((m) => ({ default: m.OpenSourcePage })),
)

const localAiUnavailableFallback = (
  <UnavailableInNativeApp
    featureName="IA no dispositivo"
    reason="O download e a execução de modelos de IA no dispositivo exigem WebGPU e armazenamento local, recursos não disponíveis no app Android. Use a versão web do Synap para configurar a IA local."
    pwaPath="/doctor/settings/local-ai"
  />
)

const anatomyUnavailableFallback = (
  <UnavailableInNativeApp
    featureName="Atlas Humano"
    reason="O Atlas Humano usa gráficos 3D no navegador (WebGL), recurso indisponível no aplicativo nativo. Use a versão web do Synap para abrir o atlas."
    pwaPath="/anatomy"
  />
)

const LoadingFallback = () => (
  <div className="flex min-h-[40dvh] items-center justify-center">
    <HeartbeatLoader label="Carregando..." />
  </div>
)

interface RouteRenderBoundaryState {
  hasError: boolean
  message: string
  chunkRecovery: boolean
}

class RouteRenderBoundary extends Component<{ children: ReactNode }, RouteRenderBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = {
      hasError: false,
      message: '',
      chunkRecovery: false,
    }
  }

  static getDerivedStateFromError(error: unknown): RouteRenderBoundaryState {
    if (isChunkLoadError(error)) {
      return {
        hasError: false,
        message: '',
        chunkRecovery: true,
      }
    }

    const message =
      error instanceof Error && error.message
        ? error.message
        : 'Erro inesperado ao abrir esta página.'

    return {
      hasError: true,
      message,
      chunkRecovery: false,
    }
  }

  componentDidCatch(error: unknown) {
    if (isChunkLoadError(error)) {
      logClientError('Chunk obsoleto — recuperação automática', error)
      recoverFromStaleDeployment()
      return
    }

    logClientError('Erro ao renderizar rota', error)
    if (
      error instanceof AppError &&
      (error.code === 'UNAUTHORIZED' || error.code === 'FORBIDDEN' || error.code === 'NOT_FOUND')
    ) {
      return
    }
    Sentry.captureException(error)
  }

  render() {
    if (this.state.chunkRecovery) {
      return (
        <div className="flex min-h-[40dvh] items-center justify-center">
          <HeartbeatLoader label="Carregando..." />
        </div>
      )
    }

    if (this.state.hasError) {
      return (
        <div className="flex min-h-[40dvh] items-center justify-center px-4">
          <div className="max-w-xl space-y-2 rounded-2xl border border-[var(--color-wine-soft-border)] bg-[var(--color-wine-soft)] px-4 py-3">
            <ErrorMessageWithCopy
              message={`Não foi possível carregar esta página.\n${this.state.message}`}
            />
            <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex min-h-10 items-center justify-center rounded-xl border border-[var(--color-border-strong)] bg-white px-3 text-xs font-semibold text-[var(--color-text-primary)]"
            >
              Recarregar aplicativo
            </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const Lazy = ({ children }: { children: ReactNode }) => (
  <RouteRenderBoundary>
    <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
  </RouteRenderBoundary>
)

const ProtectedRoutes = () => {
  const user = useSessionStore((state) => state.user)
  const sessionRestored = useSessionStore((state) => state.sessionRestored)
  const location = useLocation()
  const signedIn = Boolean(user?.onboardingCompleted && user.onboardingWelcomeSeen)

  useEffect(() => {
    if (signedIn) clearPendingIntent()
  }, [signedIn])

  if (!sessionRestored) {
    return (
      <div className="flex min-h-[40dvh] items-center justify-center">
        <HeartbeatLoader label="Carregando..." />
      </div>
    )
  }

  if (!user) {
    savePendingIntent(`${location.pathname}${location.search}${location.hash}`)
    return <Navigate to="/login" replace />
  }

  if (!user.onboardingCompleted) {
    return <Navigate to="/select-profile" replace />
  }

  if (!user.onboardingWelcomeSeen) {
    return <WelcomeScreen userName={user.name} />
  }

  return <AppShell />
}

const WelcomeScreen = ({ userName }: { userName?: string }) => {
  const handleComplete = useCallback(() => {
    void profilesRepository.markWelcomeSeen()
    useSessionStore.setState((state) =>
      state.user ? { user: { ...state.user, onboardingWelcomeSeen: true } } : state,
    )
  }, [])

  return <OnboardingWelcome userName={userName} onComplete={handleComplete} />
}

const PublicOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useSessionStore((state) => state.user)

  if (user) {
    if (!user.onboardingCompleted) {
      return <Navigate to="/select-profile" replace />
    }
    return <Navigate to={postLoginPath()} replace />
  }

  return <>{children}</>
}

const OnboardingRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useSessionStore((state) => state.user)

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (user.onboardingCompleted) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

const SystemAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useSessionStore((state) => state.user)
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)

  useEffect(() => {
    if (!user) {
      queueMicrotask(() => setIsAdmin(false))
      return
    }
    let cancelled = false
    profilesRepository
      .getIsSystemAdmin()
      .then((ok) => {
        if (!cancelled) setIsAdmin(ok)
      })
      .catch(() => {
        if (!cancelled) setIsAdmin(false)
      })
    return () => {
      cancelled = true
    }
  }, [user, user?.id])

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (isAdmin === null) {
    return (
      <div className="flex min-h-[40dvh] items-center justify-center">
        <HeartbeatLoader label="Carregando..." />
      </div>
    )
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

/** Redireciona para a home caso o perfil não possa acessar o chat com IA (ex.: paciente). */
const AiChatRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const user = useSessionStore((state) => state.user)

  if (!user || !canUseAiChat(user.role)) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

export const AppRoutes = () => (
  <Routes>
    {/* Public auth routes */}
    <Route path="/login" element={<PublicOnlyRoute><PageTransition><LoginPage /></PageTransition></PublicOnlyRoute>} />
    <Route path="/register" element={<PublicOnlyRoute><PageTransition><Lazy><RegisterPage /></Lazy></PageTransition></PublicOnlyRoute>} />
    <Route path="/forgot-password" element={<PublicOnlyRoute><PageTransition><Lazy><ForgotPasswordPage /></Lazy></PageTransition></PublicOnlyRoute>} />
    <Route path="/reset-password" element={<PageTransition><Lazy><ResetPasswordPage /></Lazy></PageTransition>} />
    <Route path="/confirm-email" element={<PageTransition><Lazy><ConfirmEmailPage /></Lazy></PageTransition>} />
    <Route path="/policy" element={<PageTransition><Lazy><PolicyPage /></Lazy></PageTransition>} />
    <Route path="/exam-share/:token" element={<PageTransition><Lazy><ExamSharePage /></Lazy></PageTransition>} />
    <Route path="/r/:token" element={<PageTransition><Lazy><PublicDocumentSharePage /></Lazy></PageTransition>} />
    <Route path="/rx/:token" element={<PageTransition><Lazy><DispensationCounterPage /></Lazy></PageTransition>} />

    {/* OAuth callbacks (VIDaaS / ITI CAS) — PÚBLICOS: o retorno da autorização chega num contexto de
        navegador externo/custom-tab que muitas vezes NÃO tem sessão Synap. Se ficassem sob o guard
        autenticado, o ProtectedRoutes redirecionaria pra /login e a página de callback nunca gravaria
        o code OAuth, prendendo o usuário no splash/login e perdendo a autorização. Aqui elas sempre
        renderizam, capturam o code e o entregam ao fluxo de assinatura. */}
    <Route path="/auth/callback" element={<PageTransition><Lazy><AuthOAuthCallbackPage /></Lazy></PageTransition>} />
    <Route path="/auth/vidaas/callback" element={<PageTransition><Lazy><VidaasCallbackPage /></Lazy></PageTransition>} />
    <Route path="/auth/iti/callback" element={<PageTransition><Lazy><ItiCallbackPage /></Lazy></PageTransition>} />

    {/* Onboarding (mandatory post-register, before accessing app) */}
    <Route path="/select-profile" element={<OnboardingRoute><PageTransition><Lazy><SelectProfilePage /></Lazy></PageTransition></OnboardingRoute>} />

    {/* Protected app routes */}
    <Route path="/" element={<ProtectedRoutes />}>
      <Route index element={<Navigate to="/dashboard" replace />} />
      <Route path="dashboard" element={<Lazy><DashboardPage /></Lazy>} />
      <Route path="patients" element={<Lazy><PatientsPage /></Lazy>} />
      <Route path="patients/:patientId" element={<Lazy><PatientDetailPage /></Lazy>} />
      <Route path={LEGACY_DOCUMENT_SIGN_PATH} element={<LegacyDocumentSignRedirect />} />
      {/* Layout único: filhos empilhados não desmontam o formulário ao navegar. */}
      <Route path="documentos" element={<Lazy><PrescriptionsPage /></Lazy>}>
        <Route path="historico" element={<></>} />
        <Route path="modelos" element={<></>} />
        <Route path="editar/:documentId" element={<></>} />
      </Route>
      <Route path="criar/:tipo" element={<IntentRoute />} />
      <Route path="inventory/new" element={<Lazy><InventoryAddPage /></Lazy>} />
      <Route path="inventory/:stockLineId/edit" element={<Lazy><InventoryStockEditPage /></Lazy>} />
      <Route path="inventory" element={<Lazy><InventoryPage /></Lazy>} />
      <Route path="prescricoes" element={<Navigate to="/documentos" replace />} />
      <Route path="receitas" element={<Navigate to="/documentos" replace />} />
      <Route path="add-exam" element={<Lazy><AddExamPage /></Lazy>} />
      <Route path="add-encounter" element={<Lazy><AddEncounterPage /></Lazy>} />
      <Route path="calendar" element={<Lazy><CalendarPage /></Lazy>} />
      <Route path="noticias" element={<Lazy><NewsPage /></Lazy>} />
      <Route path="plantoes" element={<Lazy><PlantoesPage /></Lazy>} />
      <Route path="plantoes/registros" element={<Lazy><PlantoesRegistrosPage /></Lazy>} />
      <Route path="plantoes/registros/:entryId" element={<Lazy><PlantoesRegistrosPage /></Lazy>} />
      <Route path="last-results" element={<Lazy><LastResultsPage /></Lazy>} />
      <Route
        path="chat-ia"
        element={(
          <AiChatRouteGuard>
            <Lazy><ChatIaPage /></Lazy>
          </AiChatRouteGuard>
        )}
      />
      <Route
        path="anatomy"
        element={(
          <NativeRouteGuard
            available={nativeFeatureFlags.IS_ANATOMY_ATLAS_AVAILABLE}
            fallback={anatomyUnavailableFallback}
          >
            <Lazy>
              <AnatomyPage />
            </Lazy>
          </NativeRouteGuard>
        )}
      />
      <Route path="calculator" element={<Lazy><MedicationCalculatorPage /></Lazy>} />
      <Route path="medications/:id" element={<Lazy><MedicationDetailPage /></Lazy>} />
      <Route path="settings" element={<Lazy><SettingsPage /></Lazy>} />
      <Route path="settings/profile" element={<Lazy><MyProfilePage /></Lazy>} />
      <Route path="doctor/settings/certificate" element={<Lazy><CertificateSettings /></Lazy>} />
      <Route
        path="doctor/settings/local-ai"
        element={(
          <NativeRouteGuard
            available={nativeFeatureFlags.IS_LOCAL_AI_AVAILABLE}
            fallback={localAiUnavailableFallback}
          >
            <Lazy>
              <LocalAISettingsPage />
            </Lazy>
          </NativeRouteGuard>
        )}
      />
      <Route path="settings/certificate" element={<Navigate to="/doctor/settings/certificate" replace />} />
      <Route
        path="settings/local-ai"
        element={(
          <NativeRouteGuard
            available={nativeFeatureFlags.IS_LOCAL_AI_AVAILABLE}
            fallback={localAiUnavailableFallback}
          >
            <Navigate to="/doctor/settings/local-ai" replace />
          </NativeRouteGuard>
        )}
      />
      <Route path="settings/google-calendar" element={<Lazy><GoogleCalendarPage /></Lazy>} />
      <Route path="settings/google-calendar/callback" element={<Lazy><GoogleCalendarCallbackPage /></Lazy>} />
      <Route path="settings/feed" element={<Lazy><FeedPreferencesPage /></Lazy>} />
      <Route path="settings/open-source" element={<Lazy><OpenSourcePage /></Lazy>} />
      <Route path="documentation" element={<Lazy><DocumentationPage /></Lazy>} />
      <Route path="documentation/novidades" element={<Lazy><ChangelogPage /></Lazy>} />
      <Route path="feedback" element={<Lazy><FeedbackPage /></Lazy>} />
      <Route path="notifications" element={<Lazy><NotificationsPage /></Lazy>} />
      <Route
        path="settings/admin-users"
        element={(
          <SystemAdminRoute>
            <Lazy><AdminUsersLicensesPage /></Lazy>
          </SystemAdminRoute>
        )}
      />
      <Route
        path="settings/ai-providers"
        element={(
          <SystemAdminRoute>
            <Lazy><AiProvidersAdminPage /></Lazy>
          </SystemAdminRoute>
        )}
      />
      <Route
        path="settings/admin-platform/prompts"
        element={(
          <SystemAdminRoute>
            <Lazy><PlatformPromptsAdminPage /></Lazy>
          </SystemAdminRoute>
        )}
      />
      <Route path="settings/cadastros" element={<Lazy><CadastrosPage /></Lazy>} />
      <Route path="settings/cadastros/exam-types" element={<Lazy><ExamTypesPage /></Lazy>} />
      <Route path="settings/cadastros/exam-types/edit" element={<Lazy><ExamTypeEditPage /></Lazy>} />
      <Route path="settings/cadastros/locations" element={<Lazy><LocationsPage /></Lazy>} />
      <Route path="settings/cadastros/locations/edit" element={<Lazy><LocationEditPage /></Lazy>} />
      <Route path="settings/cadastros/attendance-types" element={<Lazy><AttendanceTypesPage /></Lazy>} />
      <Route path="settings/cadastros/attendance-types/edit" element={<Lazy><AttendanceTypeEditPage /></Lazy>} />
      <Route path="settings/cadastros/clinical-conducts" element={<Lazy><ClinicalConductsPage /></Lazy>} />
      <Route path="settings/cadastros/active-ingredients" element={<Lazy><ActiveIngredientsPage /></Lazy>} />
      <Route
        path="settings/cadastros/active-ingredients/view/:ingredientId"
        element={<Lazy><ActiveIngredientViewPage /></Lazy>}
      />
      <Route path="settings/cadastros/active-ingredients/edit" element={<Lazy><ActiveIngredientEditPage /></Lazy>} />
      <Route path="settings/cadastros/cid-codes" element={<Lazy><CIDCodesPage /></Lazy>} />
      <Route path="settings/cadastros/cid-codes/edit" element={<Lazy><CIDCodeEditPage /></Lazy>} />
      <Route path="settings/cadastros/procedure-codes" element={<Lazy><ProcedureCodesPage /></Lazy>} />
      <Route path="settings/cadastros/procedure-codes/edit" element={<Lazy><ProcedureCodeEditPage /></Lazy>} />
      <Route path="settings/import/cid" element={<Lazy><ImportCIDPage /></Lazy>} />
      <Route path="settings/import/procedure-codes" element={<Lazy><ImportProcedureCodesPage /></Lazy>} />
      <Route path="settings/import" element={<Lazy><ImportCenterPage /></Lazy>} />
      <Route path="settings/import/patients" element={<Lazy><ImportPatientsPage /></Lazy>} />
      <Route path="settings/import/exams" element={<Lazy><ImportExamsPage /></Lazy>} />
      <Route path="settings/import/document-models" element={<Lazy><ImportDocumentModelsPage /></Lazy>} />
      <Route path="settings/import/ai-prompts" element={<Lazy><AiPromptsConfigPage /></Lazy>} />
      <Route path="settings/import/summarize-prompt" element={<Navigate to="/settings/import/ai-prompts" replace />} />
      <Route path="profile" element={<Navigate to="/settings/profile" replace />} />
    </Route>

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
)
